import React, { useEffect, useState } from "react";
import ButtonPrimary from "../components/Button-primary";

const DEFAULT_TP_PLANS = {
  2: [50, 50],
  3: [40, 30, 30],
  4: [20, 30, 30, 20],
  5: [15, 20, 30, 20, 15],
};

const getTPPlan = (numberOfTPs) => {
  const tpCount = Number(numberOfTPs);
  if (!Number.isInteger(tpCount) || tpCount < 2) return [];

  if (DEFAULT_TP_PLANS[tpCount]) {
    return [...DEFAULT_TP_PLANS[tpCount]];
  }

  const defaultValue = Math.floor(100 / tpCount);
  const plan = Array(tpCount).fill(defaultValue);
  plan[plan.length - 1] = 100 - (defaultValue * (tpCount - 1));
  return plan;
};

const Futures = () => {
  const [balance, setBalance] = useState(() => {
    // Récupérer la balance du cache ou initialiser à 0
    const savedBalance = localStorage.getItem("balance");
    return savedBalance ? Number(savedBalance) : 0;
  });

  const [risk, setRisk] = useState(() => {
    // Récupérer le risque du cache ou initialiser à 0
    const savedRisk = localStorage.getItem("risk");
    return savedRisk ? Number(savedRisk) : 0;
  });

  // Sauvegarder la balance dans le cache à chaque changement
  useEffect(() => {
    localStorage.setItem("balance", balance);
  }, [balance]);

  // Sauvegarder le risque dans le cache à chaque changement
  useEffect(() => {
    localStorage.setItem("risk", risk);
  }, [risk]);

  const [entry, setEntry] = useState(0);
  const [margin, setMargin] = useState(0);
  const [stop, setStop] = useState(0);
  const [tradeDirection, setTradeDirection] = useState("long");
  const [leverage, setLeverage] = useState(1);

  const [showTPModal, setShowTPModal] = useState(false);
  const [numberOfTPs, setNumberOfTPs] = useState(2);
  const [tpInputs, setTpInputs] = useState(DEFAULT_TP_PLANS[2]);
  const [totalPercentage, setTotalPercentage] = useState(100);

  const [selectedTPPlan, setSelectedTPPlan] = useState(DEFAULT_TP_PLANS[2]);
  const [manualPositionSize, setManualPositionSize] = useState("");

  const calculateRiskCapital = () => {
    if (balance === 0 || risk === 0) {
      return "Please enter all the values";
    }
    return `$${(balance * (risk / 100)).toFixed(2)}`;
  };

  const calculateMaxPositionSize = () => {
    if (balance === 0 || risk === 0 || entry === 0 || stop === 0) {
      return 0;
    }
    const riskCapital = balance * (risk / 100);
    return riskCapital / Math.abs(entry - stop);
  };

  const calculateMargin = () => {
    if (balance === 0 || risk === 0 || entry === 0 || stop === 0) {
      return "-";
    }

    const positionSize = Math.abs(calculateMaxPositionSize());
    const margin = (positionSize * entry) / leverage;
    
    return Number(margin.toFixed(2));
  };

  useEffect(() => {
    const marginValue = calculateMargin();
    setMargin(marginValue);
  }, [balance, risk, entry, stop, leverage]);

  const calculatePositionSize = () => {
    if (balance === 0 || risk === 0 || entry === 0 || stop === 0) {
      return "-";
    }

    const margin = calculateMargin();
    const positionSize = (margin * leverage) / entry;

    const digits = Math.max(0, 6 - Math.floor(Math.log10(positionSize)) - 1);
    const formattedPositionSize = isNaN(positionSize)
      ? "-"
      : `${positionSize
          .toFixed(Math.min(Math.max(digits, 0), 100))
          .replace(/\.?0+$/, "")}`;

    return formattedPositionSize;
  };

  const getActivePositionSize = () => {
    if (manualPositionSize.trim() !== "") {
      const manualValue = Number(manualPositionSize.replace(",", "."));
      return Number.isFinite(manualValue) && manualValue > 0 ? manualValue : null;
    }

    const calculatedValue = calculatePositionSize();
    return calculatedValue === "-" ? null : Number(calculatedValue);
  };

  const calculateLiquidationPrice = () => {
    if (entry === 0) {
      return "-";
    }

    const maintenanceMarginRate = 0.004; // 0.4%
    // const positionSize = Math.abs(calculateMaxPositionSize());

    // Formule simplifiée pour le prix de liquidation
    let liquidationPrice;
    
    if (tradeDirection === "long") {
      // Pour les longs : EP * (1 - IMR - MMR)
      liquidationPrice = entry * (1 - (1/leverage) - maintenanceMarginRate);
    } else {
      // Pour les shorts
      liquidationPrice = entry * (1 + (1/leverage) + maintenanceMarginRate);
    }

    return `$${liquidationPrice.toFixed(2)}`;
  };

  const calculateMaxLeverage = () => {
    if (!entry || !stop) return 0;

    const maintenanceMarginRate = tradeDirection === "long" 
      ? 0.004 
      : 0.005;
    let maxLeverage = 1;
    // const positionSize = Math.abs(calculateMaxPositionSize());

    for (let testLeverage = 1; testLeverage <= 100; testLeverage++) {
      // const maintenanceMargin = entry * positionSize * maintenanceMarginRate;
      // const positionMargin = (entry * positionSize) / testLeverage;
      
      const liquidationPrice = tradeDirection === "long"
        ? entry * (1 - (1/testLeverage) - maintenanceMarginRate)
        : entry * (1 + (1/testLeverage) + maintenanceMarginRate);

      if (tradeDirection === "long" && liquidationPrice >= stop) {
        break;
      }
      if (tradeDirection === "short" && liquidationPrice <= stop) {
        break;
      }
      maxLeverage = testLeverage;
    }
    
    return maxLeverage;
  };

  // const getDisplayMaxLeverage = () => {
  //   return Math.min(100, calculateMaxLeverage());
  // };

  const generateTPInputs = () => {
    const newInputs = getTPPlan(numberOfTPs);
    if (newInputs.length === 0) return;

    setTpInputs(newInputs);
    setSelectedTPPlan(newInputs);
    calculateTotal(newInputs);
  };

  const handleNumberOfTPsChange = (value) => {
    const newNumberOfTPs = Number(value);
    setNumberOfTPs(newNumberOfTPs);

    const newPlan = getTPPlan(newNumberOfTPs);
    if (newPlan.length > 0) {
      setTpInputs(newPlan);
      setSelectedTPPlan(newPlan);
      calculateTotal(newPlan);
    }
  };

  const updateTP = (index, value) => {
    const newInputs = [...tpInputs];
    newInputs[index] = Number(value);
    setTpInputs(newInputs);
    setSelectedTPPlan(newInputs);
    calculateTotal(newInputs);
  };

  const calculateTotal = (inputs) => {
    const total = inputs.reduce((acc, curr) => acc + Number(curr), 0);
    setTotalPercentage(total);
  };

  const savePlan = () => {
    if (totalPercentage !== 100) return;
    
    const savedPlans = JSON.parse(localStorage.getItem('tpPlans') || '{}');
    savedPlans[numberOfTPs] = tpInputs;
    localStorage.setItem('tpPlans', JSON.stringify(savedPlans));
    setSelectedTPPlan(tpInputs);
    setShowTPModal(false);
  };

  const loadSavedPlans = () => {
    return JSON.parse(localStorage.getItem('tpPlans') || '{}');
  };

  const handleTPPlanSelection = (value) => {
    if (!value) {
      setSelectedTPPlan(null);
      return;
    }

    if (value.startsWith("preset-")) {
      const presetNumber = Number(value.replace("preset-", ""));
      const preset = getTPPlan(presetNumber);
      setNumberOfTPs(presetNumber);
      setTpInputs(preset);
      setSelectedTPPlan(preset);
      calculateTotal(preset);
      return;
    }

    const savedPlan = loadSavedPlans()[value];
    setNumberOfTPs(Number(value));
    setTpInputs(savedPlan);
    setSelectedTPPlan(savedPlan);
    calculateTotal(savedPlan);
  };

  const activePositionSize = getActivePositionSize();

  return (
    <>
      <div className="flex justify-center max-w-md gap-1 mx-auto">
        <ButtonPrimary text={"Long"} className={"!bg-green-600 hover:!bg-green-500 active:bg-green-700"} func={() => setTradeDirection("long")}/>
        <ButtonPrimary text={"Short"} className={"bg-red-600 hover:bg-red-500 active:bg-red-700"} func={() => setTradeDirection("short")}/>
      </div>

      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-wrap max-w-md gap-2 px-4 mx-auto mt-2"
      >
        <div className="flex flex-col justify-around w-full gap-4 p-4 border-2 border-gray-200 rounded-md md:flex-row">
          <div className="relative flex flex-col w-full md:w-5/12">
            <label>Balance</label>
            <input
              type="text"
              placeholder="Balance amount"
              className="input-1"
              value={balance || ''}
              onChange={(e) => setBalance(Number(e.target.value.replace(",", ".")))}
            />
          </div>
          <div className="relative flex flex-col w-full md:w-5/12">
            <label>Risk (%)</label>
            <input
              type="text"
              placeholder="Risk"
              className="input-1"
              value={risk || ''}
              onChange={(e) => setRisk(e.target.value.replace(",", "."))}
            />
          </div>
        </div>
        <div className="flex flex-wrap justify-around w-full gap-4 p-4 border-2 border-gray-200 rounded-md">
          <div className="relative flex flex-col w-full md:w-5/12">
            <label>Entry price</label>
            <input
              type="text"
              placeholder="Entry price"
              className="input-1"
              onChange={(e) =>
                setEntry(Number(e.target.value.replace(",", ".")))
              }
            />
          </div>
          <div className="relative flex flex-col w-full md:w-5/12">
            <label>Stop loss</label>
            <input
              type="text"
              placeholder="Stop loss"
              className="input-1"
              onChange={(e) =>
                setStop(Number(e.target.value.replace(",", ".")))
              }
            />
          </div>
          <div className="relative flex flex-col w-full md:px-4">
            <label>Leverage</label>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="w-full input-1"
                  value={leverage}
                  placeholder="Custom leverage"
                  onChange={(e) => {
                    const value = Math.min(100, Math.max(1, Number(e.target.value)));
                    setLeverage(value);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => setLeverage(calculateMaxLeverage())}
                className={`px-4 py-2 rounded-md ${
                  leverage === calculateMaxLeverage() 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
              >
                Max {calculateMaxLeverage()}x
              </button>
            </div>
          </div>
        </div>

        {tradeDirection === "long" && (
          <div className="relative w-full">
            <div
              className={`border-2 border-green-200 bg-green-50 rounded-md p-4 w-full flex flex-col justify-around ${
                stop > entry || (leverage > calculateMaxLeverage() && Number(calculateLiquidationPrice().replace('$', '')) > stop)
                  ? "blur-sm" 
                  : ""
              }`}
            >
              <div className="flex justify-between w-full">
                <p>Risk Capital</p>
                <p>{calculateRiskCapital()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Margin</p>
                <p>${margin}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Position size</p>
                <p>{calculatePositionSize()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Liquidation</p>
                <p>{calculateLiquidationPrice()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Margin used (%)</p>
                <p>
                  {isNaN(Number(margin / balance) * 100) 
                    ? "-" 
                    : `${(Number(margin / balance) * 100).toFixed(2)}%`}
                </p>
              </div>
            </div>
            {stop > entry ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="p-4 text-xs font-bold text-red-500 bg-white border border-black rounded-lg">
                  The stop should be lower than the entry.
                </span>
              </div>
            ) : leverage > calculateMaxLeverage() && Number(calculateLiquidationPrice().replace('$', '')) > stop ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="p-4 text-xs font-bold text-red-500 bg-white border border-black rounded-lg">
                  Warning: Liquidation price (${calculateLiquidationPrice().replace('$', '')}) is higher than your stop loss (${stop}).<br/>
                  Maximum safe leverage: {calculateMaxLeverage()}x
                  <button
                    onClick={() => setLeverage(calculateMaxLeverage())}
                    className="block w-full px-3 py-1 mt-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                  >
                    Set to max safe leverage
                  </button>
                </span>
              </div>
            ) : null}
          </div>
        )}
        {tradeDirection === "short" && (
          <div className="relative w-full">
            <div
              className={`border-2 border-red-200 bg-red-50 rounded-md p-4 w-full flex flex-col justify-around ${
                stop < entry || (leverage > calculateMaxLeverage() && Number(calculateLiquidationPrice().replace('$', '')) < stop)
                  ? "blur-sm"
                  : ""
              }`}
            >
              <div className="flex justify-between w-full">
                <p>Risk Capital</p>
                <p>{calculateRiskCapital()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Margin</p>
                <p>{margin}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Position size</p>
                <p>{calculatePositionSize()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Liquidation</p>
                <p>{calculateLiquidationPrice()}</p>
              </div>
              <div className="flex justify-between w-full">
                <p>Margin used (%)</p>
                <p>
                  {isNaN(Number(margin / balance) * 100) 
                    ? "-" 
                    : `${(Number(margin / balance) * 100).toFixed(2)}%`}
                </p>
              </div>
            </div>
            {stop < entry ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="p-4 text-xs font-bold text-red-500 bg-white border border-black rounded-lg">
                  The entry should be higher than the stop.
                </span>
              </div>
            ) : leverage > calculateMaxLeverage() ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="p-4 text-xs font-bold text-red-500 bg-white border border-black rounded-lg">
                  Warning: Liquidation price (${calculateLiquidationPrice().replace('$', '')}) is lower than your stop loss (${stop}).<br/>
                  Maximum safe leverage: {calculateMaxLeverage()}x
                  <button
                    onClick={() => setLeverage(calculateMaxLeverage())}
                    className="block w-full px-3 py-1 mt-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                  >
                    Set to max safe leverage
                  </button>
                </span>
              </div>
            ) : null}
          </div>
        )}
      </form>

      <div className="flex justify-center max-w-md gap-1 mx-auto mt-2">
        <button 
          onClick={() => setShowTPModal(true)}
          className="w-1/2 px-4 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          TP Plans Settings
        </button>
        <select 
          onChange={(e) => handleTPPlanSelection(e.target.value)}
          className="w-1/2 px-3 py-2 border rounded-md"
        >
          <option value="">Select TP Plan</option>
          <optgroup label="Presets par défaut">
            {Object.entries(DEFAULT_TP_PLANS).map(([numTPs, percentages]) => (
              <option key={`preset-${numTPs}`} value={`preset-${numTPs}`}>
                {numTPs} TPs ({percentages.join("/")})
              </option>
            ))}
          </optgroup>
          {Object.keys(loadSavedPlans()).length > 0 && (
            <optgroup label="Plans sauvegardés">
              {Object.keys(loadSavedPlans()).map(numTPs => (
                <option key={numTPs} value={numTPs}>{numTPs} TPs</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      <div className="max-w-md p-4 mx-auto mt-2 border-2 border-gray-200 rounded-md">
        <label htmlFor="manual-position-size" className="block mb-1">
          Position size manuelle (quantité)
        </label>
        <input
          id="manual-position-size"
          type="text"
          inputMode="decimal"
          placeholder="Ex. 125.50"
          value={manualPositionSize}
          onChange={(e) => setManualPositionSize(e.target.value)}
          className="w-full input-1"
        />
        <p className="mt-1 text-xs text-gray-500">
          Laissez vide pour utiliser la taille calculée par le calculateur.
        </p>
        {manualPositionSize.trim() !== "" && activePositionSize === null && (
          <p className="mt-2 text-sm text-red-500">
            Entrez une quantité supérieure à 0.
          </p>
        )}
      </div>

      {showTPModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">TP Plans Configuration</h2>
              <button 
                onClick={() => setShowTPModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="pb-4 mb-6 border-b">
              <h3 className="mb-2 font-semibold">Saved Plans</h3>
              <div className="flex flex-wrap justify-around gap-2">
                {Object.entries(loadSavedPlans()).map(([numTPs, percentages]) => (
                  <div key={numTPs} className="flex items-center gap-2 p-2 rounded bg-gray-50">
                    <span className="text-sm">{numTPs} TPs</span>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => {
                          setNumberOfTPs(Number(numTPs));
                          setTpInputs(percentages);
                          calculateTotal(percentages);
                        }}
                        className="px-2 py-1 text-xs text-white bg-blue-500 rounded hover:bg-blue-600"
                      >
                        <i class="fa-regular fa-pen-to-square"></i>
                      </button>
                      <button 
                        onClick={() => {
                          const plans = loadSavedPlans();
                          delete plans[numTPs];
                          localStorage.setItem('tpPlans', JSON.stringify(plans));
                          if (selectedTPPlan && selectedTPPlan.length === Number(numTPs)) {
                            setSelectedTPPlan(null);
                          }
                        }}
                        className="px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <div className="flex-1">
                <input 
                  type="number"
                  min="2"
                  placeholder="Number of TPs"
                  value={numberOfTPs}
                  onChange={(e) => handleNumberOfTPsChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <button 
                onClick={generateTPInputs}
                className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
              >
                Generate
              </button>
            </div>

            <div className="p-3 mb-4 text-sm rounded-md bg-gray-50">
              <p className="mb-1 font-semibold">Presets par défaut :</p>
              <p>3 TP : 40/30/30</p>
              <p>4 TP : 20/30/30/20</p>
              <p>5 TP : 15/20/30/20/15</p>
            </div>

            <div className="mb-4 space-y-2">
              {tpInputs.map((tp, index) => (
                <div key={index} className="flex items-center gap-2">
                  <label className="w-16">TP {index + 1}</label>
                  <input 
                    type="number"
                    value={tp}
                    onChange={(e) => updateTP(index, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-md"
                  />
                  <span className="w-8">%</span>
                </div>
              ))}
            </div>

            {totalPercentage !== 100 && (
              <div className="mb-4 text-red-500">
                Total must be 100% (current: {totalPercentage}%)
              </div>
            )}

            <button 
              onClick={savePlan}
              disabled={totalPercentage !== 100}
              className={`w-full py-2 rounded-md ${
                totalPercentage === 100 
                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Save Plan
            </button>
          </div>
        </div>
      )}

      {selectedTPPlan && activePositionSize !== null && (
        <div className="max-w-md gap-2 p-4 mx-auto mt-2 space-y-2 rounded-md bg-gray-50">
          <div className="flex justify-between pb-2 mb-2 border-b">
            <span>Position size utilisée</span>
            <span>{activePositionSize}</span>
          </div>
          {selectedTPPlan.map((percentage, index) => {
            const tpQuantity = activePositionSize * (percentage / 100);
            return (
              <div key={index} className="flex justify-between">
                <span>TP{index + 1} ({percentage}%)</span>
                <span>{tpQuantity.toFixed(4)}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Futures;
